import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { startTransition, useOptimistic } from "react";
import {
  ArrivingFailed,
  ArrivingLayout,
  ArrivingPending,
} from "@/client/components/arriving-layout";
import { ConfirmIdentityDialog } from "@/client/components/confirm-identity";
import { pageTitle } from "@/client/lib/page-title";
import { PENDING_MS } from "@/client/lib/pending";
import { sessionQuery } from "@/client/lib/session";
import { invitableRoles } from "@/features/invitation/roles";
import { useInvitePeople } from "@/features/invitation/use-invite-people";
import { InviteStep } from "@/features/onboarding/components/invite-step";
import { SettingUp } from "@/features/onboarding/components/setting-up";
import { useOnboardingStep } from "@/features/onboarding/use-onboarding-step";

export const Route = createFileRoute("/_authenticated/onboarding/invite")({
  pendingMs: PENDING_MS,
  pendingComponent: ArrivingPending,
  errorComponent: ArrivingFailed,
  head: async () => {
    const { onboardingCopy } = await import("@/features/onboarding/copy");
    return { meta: [{ title: pageTitle(onboardingCopy.invite.title) }] };
  },
  component: OnboardingInvite,
});

function OnboardingInvite() {
  // Read by the parent guard, so this does not suspend.
  const { data: session } = useSuspenseQuery(sessionQuery);
  const { moveOn } = useOnboardingStep();
  // Shown only while the step is finishing; a refusal puts the form back with its sentence.
  const [settingUp, showSettingUp] = useOptimistic(false);
  const flow = useInvitePeople(
    () => {},
    () => {
      startTransition(() => showSettingUp(true));
      return moveOn();
    },
  );
  const company = session.companyName ?? "";

  return (
    <ArrivingLayout>
      {settingUp ? <SettingUp company={company} /> : null}
      {/* Kept mounted so a refused finish leaves the typed addresses in place. */}
      <div hidden={settingUp}>
        <InviteStep
          roles={session.role ? invitableRoles(session.role) : []}
          act={flow.act}
          busy={flow.busy}
          problem={flow.state.problem}
          refused={flow.state.refusedRow !== null}
        />
      </div>
      <ConfirmIdentityDialog
        ask={flow.state.ask}
        busy={flow.busy}
        onConfirm={flow.confirm}
        onCancel={flow.cancel}
      />
    </ArrivingLayout>
  );
}
